// Field schemas for each service booking
export const SERVICE_FIELDS = {
  boxing: {
    title: "Boxing Match Booking",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
      { name: "address", label: "Address", type: "text", required: true },
      { name: "event", label: "Event / Match", type: "text", placeholder: "e.g. Twinstars Championship Night", required: true },
      { name: "event_date", label: "Event date", type: "date", required: true },
      { name: "tickets", label: "Tickets quantity", type: "number", min: 1, default: 1, required: true },
      { name: "tier", label: "Ticket tier", type: "select", options: ["Standard", "VIP", "Ringside"], required: true },
      { name: "id_upload", label: "ID upload", type: "file", required: false },
    ],
    pricing: { Standard: 50, VIP: 150, Ringside: 300 },
    priceField: (d) => (parseInt(d.tickets || 1) * (({ Standard: 50, VIP: 150, Ringside: 300 })[d.tier] || 0)),
  },
  spa: {
    title: "Spa Booking",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "address", label: "Address", type: "text", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
      { name: "service_type", label: "Preferred service", type: "select", options: ["Facial", "Massage", "Acupuncture", "Body Therapy"], required: true },
      { name: "preferred_date", label: "Preferred date", type: "date", required: true },
      { name: "preferred_time", label: "Preferred time", type: "time", required: true },
    ],
    pricing: { Facial: 80, Massage: 120, Acupuncture: 100, "Body Therapy": 150 },
    priceField: (d) => (({ Facial: 80, Massage: 120, Acupuncture: 100, "Body Therapy": 150 })[d.service_type] || 0),
  },
  gym: {
    title: "Gym Membership",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "address", label: "Address", type: "text", required: true },
      { name: "dob", label: "Date of birth", type: "date", required: true },
      { name: "fitness_goals", label: "Fitness goals", type: "textarea", required: true },
      { name: "plan", label: "Membership plan", type: "select", options: ["Monthly", "Quarterly", "Annual"], required: true },
      { name: "emergency_contact", label: "Emergency contact", type: "text", required: true },
      { name: "id_upload", label: "ID upload", type: "file", required: false },
    ],
    pricing: { Monthly: 60, Quarterly: 160, Annual: 550 },
    priceField: (d) => (({ Monthly: 60, Quarterly: 160, Annual: 550 })[d.plan] || 0),
  },
  guesthouse: {
    title: "Guesthouse Booking",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "address", label: "Home address", type: "text", required: true },
      { name: "nationality", label: "Nationality", type: "text", required: true },
      { name: "room_type", label: "Room type", type: "select", options: ["Standard", "Deluxe", "Suite", "Apartment"], required: true },
      { name: "check_in", label: "Check-in date", type: "date", required: true },
      { name: "check_out", label: "Check-out date", type: "date", required: true },
      { name: "guests", label: "Guests", type: "number", min: 1, default: 1, required: true },
      { name: "id_upload", label: "ID upload", type: "file", required: false },
    ],
    pricing: { Standard: 80, Deluxe: 140, Suite: 220, Apartment: 280 },
    priceField: (d) => {
      const nightly = ({ Standard: 80, Deluxe: 140, Suite: 220, Apartment: 280 })[d.room_type] || 0;
      const inD = d.check_in ? new Date(d.check_in) : null;
      const outD = d.check_out ? new Date(d.check_out) : null;
      const nights = inD && outD ? Math.max(1, Math.round((outD - inD) / (1000*60*60*24))) : 1;
      return nightly * nights;
    },
  },
  barber: {
    title: "Barbing Salon Booking",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "address", label: "Address", type: "text", required: true },
      { name: "service_type", label: "Service", type: "select", options: ["Classic Cut", "Beard Trim", "Cut + Beard", "Kids Cut"], required: true },
      { name: "appointment_date", label: "Appointment date", type: "date", required: true },
      { name: "appointment_time", label: "Appointment time", type: "time", required: true },
    ],
    pricing: { "Classic Cut": 20, "Beard Trim": 15, "Cut + Beard": 30, "Kids Cut": 18 },
    priceField: (d) => (({ "Classic Cut": 20, "Beard Trim": 15, "Cut + Beard": 30, "Kids Cut": 18 })[d.service_type] || 0),
  },
  washingbay: {
    title: "Washing Bay Booking",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "vehicle_type", label: "Vehicle type", type: "select", options: ["Sedan", "SUV", "Truck", "Bike", "Bus"], required: true },
      { name: "plate_number", label: "Plate number", type: "text", required: true },
      { name: "service_type", label: "Preferred service", type: "select", options: ["Exterior Wash", "Interior Valet", "Full Detail", "Engine Wash"], required: true },
      { name: "appointment_time", label: "Appointment time", type: "datetime-local", required: true },
    ],
    pricing: { "Exterior Wash": 12, "Interior Valet": 25, "Full Detail": 60, "Engine Wash": 30 },
    priceField: (d) => (({ "Exterior Wash": 12, "Interior Valet": 25, "Full Detail": 60, "Engine Wash": 30 })[d.service_type] || 0),
  },
  laundry: {
    title: "Laundry Request",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true },
      { name: "phone", label: "Phone number", type: "tel", required: true },
      { name: "pickup_address", label: "Pickup address", type: "text", required: true },
      { name: "laundry_type", label: "Laundry type", type: "select", options: ["Wash & Fold", "Dry Cleaning", "Ironing only", "Heavy / Linens"], required: true },
      { name: "weight_kg", label: "Estimated weight (kg)", type: "number", min: 1, default: 5, required: true },
      { name: "pickup_date", label: "Pickup date (Tuesdays only)", type: "date", required: true, note: "Pickup runs every Tuesday. Delivery returns Saturday." },
    ],
    pricing: { "Wash & Fold": 3, "Dry Cleaning": 8, "Ironing only": 2, "Heavy / Linens": 5 },
    priceField: (d) => ((parseInt(d.weight_kg || 0)) * (({ "Wash & Fold": 3, "Dry Cleaning": 8, "Ironing only": 2, "Heavy / Linens": 5 })[d.laundry_type] || 0)),
  },
};

export const JOB_FIELDS = {
  spa_therapist: [
    { name: "full_name", label: "Full name", required: true },
    { name: "address", label: "Address", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "specialty", label: "Specialty", type: "select", options: ["Facial", "Massage", "Acupuncture"], required: true },
    { name: "certification_upload", label: "Certification upload", type: "file" },
    { name: "experience", label: "Work experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  gym_trainer: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "specialty", label: "Fitness specialty", type: "select", options: ["Gym Fitness Trainer", "Spotter"], required: true },
    { name: "certification_upload", label: "Certification upload", type: "file" },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  barber: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "experience_years", label: "Years of experience", type: "number", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  car_wash_staff: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "role", label: "Role", type: "select", options: ["Car Washer", "Car Wash Attendant"], required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  laundry_staff: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "role", label: "Role", type: "select", options: ["Laundry Receptionist", "Laundry Personnel", "Driver"], required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  guesthouse_receptionist: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "certification_upload", label: "Certification upload", type: "file" },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  cleaner: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  security: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  sales_rep: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  supervisor: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
  personal_assistant: [
    { name: "full_name", label: "Full name", required: true },
    { name: "phone", label: "Phone number", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "address", label: "Address", required: true },
    { name: "sex", label: "Sex", type: "select", options: ["Male", "Female", "Other"], required: true },
    { name: "experience", label: "Experience", type: "textarea", required: true },
    { name: "id_upload", label: "ID upload", type: "file" },
  ],
};
